import { useLocation } from 'react-router-dom';
import { Disclosure, Transition } from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { NavSection, NavItem } from './navConfig';
import { getActiveNavHref } from './navConfig';
import SidebarNavItem from './SidebarNavItem';
import { useTeamId } from '../../hooks/useTeamId';
import { useTeamRole } from '../../hooks/useTeamRole';

interface SidebarNavGroupProps {
  section: NavSection;
  /** Feature-accessible items only — partitioned upstream by buildTieredNav.
   *  Locked items are consolidated into the "Unlock more" surface, not shown here. */
  items: NavItem[];
  pendingCount?: number;
}

export default function SidebarNavGroup({ section, items, pendingCount = 0 }: SidebarNavGroupProps) {
  const location = useLocation();

  // Resolve current user's team role so we can filter items that have
  // requiredRoles set (e.g. Audit Logs is OWNER + COMPLIANCE_OFFICER only).
  // Feature gating is already applied by buildTieredNav; this is role gating
  // only — a role-hidden item is hidden, never upsold.
  const { teamId } = useTeamId();
  const { role, isLoading: roleLoading } = useTeamRole(teamId);

  // Filter items by role. Fail closed: while role is loading, hide
  // role-restricted items so they don't flash for unauthorized users.
  const visibleItems = items.filter((item) => {
    if (!item.requiredRoles) return true;
    if (roleLoading || !role) return false;
    return item.requiredRoles.includes(role);
  });

  // Nothing the user can see in this section (all items role-hidden) → render nothing.
  if (visibleItems.length === 0) return null;

  // Use longest-prefix match so a child route only highlights its own section.
  const activeHref = getActiveNavHref(location.pathname);
  const isGroupActive = visibleItems.some((item) => activeHref === item.href);

  const hasBadge =
    visibleItems.some((item) => item.badge === 'pending-reviews') && pendingCount > 0;

  return (
    <Disclosure defaultOpen={isGroupActive}>
      {({ open }) => (
        <div>
          <Disclosure.Button
            className={clsx(
              'group flex w-full items-center gap-x-3 rounded-md px-3 py-2 text-left text-sm font-semibold transition-colors',
              isGroupActive
                ? 'text-ink'
                : 'text-ink-2 hover:bg-panel-2 hover:text-ink'
            )}
          >
            <section.icon
              className={clsx(
                'h-5 w-5 shrink-0 transition-colors',
                isGroupActive ? 'text-teal-600' : 'text-ink-4 group-hover:text-ink-2'
              )}
            />
            <span className="flex-1 truncate">{section.label}</span>
            {hasBadge && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
            <ChevronRightIcon
              className={clsx(
                'h-4 w-4 shrink-0 text-ink-4 transition-transform duration-150',
                open && 'rotate-90'
              )}
            />
          </Disclosure.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform opacity-0 -translate-y-1"
            enterTo="transform opacity-100 translate-y-0"
            leave="transition duration-75 ease-in"
            leaveFrom="transform opacity-100 translate-y-0"
            leaveTo="transform opacity-0 -translate-y-1"
          >
            <Disclosure.Panel className="mt-1 space-y-0.5 pl-4">
              {visibleItems.map((item) => (
                <SidebarNavItem key={item.href} item={item} pendingCount={pendingCount} />
              ))}
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  );
}
